// External Insight Provider types and Zod schemas (Issue #22).
//
// External tools emit domain-specific edges and anchor mappings; the analyzer
// library validates at trust boundaries and integrates resolved edges into the
// DependencyGraph. Configuration and provider construction live in the caller.

import { z } from "zod";

import type { DependencyGraph } from "../dependency/types.js";

// ---------------------------------------------------------------------------
// External edge & insight
// ---------------------------------------------------------------------------

export const PROPAGATION_DIRECTIONS = ["forward", "backward", "both"] as const;

export const PropagationDirectionSchema = z.enum(PROPAGATION_DIRECTIONS);

export type PropagationDirection = z.infer<typeof PropagationDirectionSchema>;

export const SymbolAnchorSchema = z.object({
  symbolId: z.string(),
  filePath: z.string(),
  startLine: z.number().int().positive(),
  endLine: z.number().int().positive(),
});

export type SymbolAnchor = z.infer<typeof SymbolAnchorSchema>;

export const ExternalEvidenceSchema = z.object({
  kind: z.string(),
  detail: z.string(),
  filePath: z.string().optional(),
  line: z.number().int().positive().optional(),
  endLine: z.number().int().positive().optional(),
  symbolId: z.string().optional(),
  llmConfidence: z.number().min(0).max(1).optional(),
});

export type ExternalEvidence = z.infer<typeof ExternalEvidenceSchema>;

export const ExternalEdgeSchema = z.object({
  from: z.string(),
  to: z.string(),
  kind: z.string(),
  propagation: PropagationDirectionSchema.default("forward"),
  weight: z.number().min(0).max(1).default(0.7),
  metadata: z.record(z.string(), z.unknown()).optional(),
  evidence: z.array(ExternalEvidenceSchema).optional(),
});

export type ExternalEdge = z.infer<typeof ExternalEdgeSchema>;

export const AnchorMappingSchema = z.object({
  domainId: z.string(),
  filePaths: z.array(z.string()),
  symbolIds: z.array(z.string()).optional(),
  symbols: z.array(SymbolAnchorSchema).optional(),
  artifactId: z.string().optional(),
});

export type AnchorMapping = z.infer<typeof AnchorMappingSchema>;

export const ExternalInsightSchema = z.object({
  source: z.string(),
  sourceVersion: z.string().optional(),
  generatedAt: z.string().optional(),
  edges: z.array(ExternalEdgeSchema),
  anchorMapping: z.array(AnchorMappingSchema).optional(),
});

export type ExternalInsight = z.infer<typeof ExternalInsightSchema>;

// ---------------------------------------------------------------------------
// Provider query & interface
// ---------------------------------------------------------------------------

export const InsightQuerySchema = z.object({
  projectRoot: z.string(),
  changedFiles: z.array(z.string()).optional(),
  changedSymbols: z.array(z.string()).optional(),
  artifactIds: z.array(z.string()).optional(),
  evidencePolicy: z
    .object({
      exclude: z.array(z.string()).optional(),
    })
    .optional(),
});

export type InsightQuery = z.infer<typeof InsightQuerySchema>;

export const ImpactNotificationSchema = z.object({
  affectedFiles: z.array(z.string()),
  affectedSymbols: z.array(z.string()).optional(),
  affectedArtifactIds: z.array(z.string()),
  impactReport: z.record(z.string(), z.unknown()),
});

export type ImpactNotification = z.infer<typeof ImpactNotificationSchema>;

/** Supplies structured insights from an external tool (speckeeper, data-lineage, …). */
export interface InsightProvider {
  readonly name: string;
  provide(query: InsightQuery): Promise<ExternalInsight>;
}

// ---------------------------------------------------------------------------
// Integration results
// ---------------------------------------------------------------------------

export const UnresolvedEdgeSchema = z.object({
  from: z.string(),
  to: z.string(),
  kind: z.string(),
  source: z.string(),
  reason: z.string(),
});

export type UnresolvedEdge = z.infer<typeof UnresolvedEdgeSchema>;

export const ProviderErrorSchema = z.object({
  providerName: z.string(),
  message: z.string(),
});

export type ProviderError = z.infer<typeof ProviderErrorSchema>;

export interface IntegrationStats {
  totalEdgesAdded: number;
  providersSucceeded: number;
  providersFailed: number;
}

export interface IntegrationResult {
  graph: DependencyGraph;
  unresolvedEdges: UnresolvedEdge[];
  providerErrors: ProviderError[];
  stats: IntegrationStats;
}
